import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { getDatabase, ref, query, orderByChild, equalTo, onValue, remove } from 'firebase/database';
import moment from 'moment';
import 'moment/locale/pt-br';

import app, { auth, googleProvider } from '../utils/firebase';

const EDIT_WINDOW_MS = 15 * 60 * 1000;

function Notes() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      setNotes([]);
      return;
    }

    setNotesLoading(true);
    const db = getDatabase(app);
    const notesQuery = query(ref(db, 'notes'), orderByChild('uid'), equalTo(user.uid));

    const unsubscribe = onValue(
      notesQuery,
      (snapshot) => {
        const data = snapshot.val();
        const list = data
          ? Object.entries(data)
              .map(([key, val]) => ({ key, ...val }))
              .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          : [];
        setNotes(list);
        setNotesLoading(false);
      },
      () => setNotesLoading(false)
    );

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(interval);
  }, []);

  const canEdit = (note) => note.createdAt && now - note.createdAt < EDIT_WINDOW_MS;

  const handleLogin = () => signInWithPopup(auth, googleProvider);
  const handleLogout = () => signOut(auth);

  const handleDelete = (note) => {
    if (!window.confirm('Excluir este bilhetinho?')) return;
    remove(ref(getDatabase(app), `notes/${note.key}`));
  };

  const firstName = user?.displayName?.split(' ')[0] || '';

  if (authLoading) {
    return (
      <div id="notes" className="d-flex align-items-center justify-content-center py-5 note">
        <h4 className="text-center note-text">...</h4>
      </div>
    );
  }

  if (!user) {
    return (
      <div id="notes" className="d-flex align-items-center justify-content-center note">
        <div className="note-login text-center">
          <h2 className="note-heading">Bilhetinhos</h2>
          <p className="note-login-text">Entre para ver e criar bilhetinhos</p>
          <button className="note-send" type="button" onClick={handleLogin}>
            Entrar com Google
          </button>
          <p className="mt-3 mb-0">
            <Link to="/">Voltar</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="notes" className="d-flex align-items-start justify-content-center py-5 note">
      <div className="container py-5 note-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="note-heading">Olá, {firstName}!</h2>
          <button className="note-send" type="button" onClick={handleLogout}>Sair</button>
        </div>

        <button className="note-send mb-4 w-100" type="button" onClick={() => navigate('/notes/new')}>
          + Novo bilhetinho
        </button>

        {notesLoading ? (
          <h4 className="text-center note-text">...</h4>
        ) : notes.length === 0 ? (
          <p className="note-from text-center mt-4">Nenhum bilhetinho ainda...</p>
        ) : (
          <div className="notes-list">
            {notes.map((note) => (
              <div key={note.key} className="note-item">
                <div className="note-item-header">
                  {note.to && <span className="note-item-to">Para: {note.to}</span>}
                  <span className="note-item-time">
                    {note.createdAt ? moment(note.createdAt).fromNow() : ''}
                  </span>
                </div>
                <div className="note-item-body">
                  <div className="note-item-body-main">
                    <a
                      className="note-item-link"
                      href={`/notes/${note.key}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="note-item-preview">{note.content?.trim()}</span>
                      <span className="note-new-tab-icon">↗</span>
                    </a>
                  </div>
                  {canEdit(note) && (
                    <div className="note-item-actions">
                      <button
                        className="note-icon-btn"
                        type="button"
                        title="Editar"
                        onClick={() => navigate('/notes/new', { state: { edit: note } })}
                      >
                        ✏
                      </button>
                      <button
                        className="note-icon-btn note-icon-delete"
                        type="button"
                        title="Excluir"
                        onClick={() => handleDelete(note)}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Notes;
